import { useQuery } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../components/Button';
import Card, { Cards } from '../../components/Card';
import { DEFAULT_DATES } from '../../components/DateRangePicker';
import EmptyState from '../../components/EmptyState';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../../components/LoadingIndicator';
import type { AuditLogType } from '../../graphql/generated/graphql';
import filterIcon from '../../icons/filter.svg';
import type { EntitySearchResult } from '../../search/entitySearch/EntitySelect';
import SettingsContainerView from '../../settings/SettingsContainerView';
import UIActions from '../../ui/UIActions';
import ConnectionHelper from '../../utils/graphql/ConnectionHelper';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import AuditFilter from './AuditFilter';
import AuditLogRow from './AuditLogRow';
import styles from './AuditLogContentView.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';

const AUDIT_VIEW_QUERY = gql(`
  query AuditLogContentView(
    $afterCursor: String
    $types: [AuditLogType!]
    $user: ID
    $startDate: Date!
    $endDate: Date!
  ) {
    currentSession {
      organization {
        id
        auditLogs(
          first: 100
          after: $afterCursor
          types: $types
          user: $user
          startDate: $startDate
          endDate: $endDate
        ) {
          edges {
            node {
              ...AuditLogRow_auditLog
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        provisions {
          ...AuditLogRow_provisions
        }
      }
    }
  }
`);

type Dates = {
  startAt: moment.Moment | null;
  endAt: moment.Moment | null;
};

const AuditLogContentView = () => {
  const [action, setAction] = useState<AuditLogType | null>(null);
  const [entity, setEntity] = useState<EntitySearchResult | null>(null);
  const [dates, setDates] = useState<Dates>({
    startAt: moment(DEFAULT_DATES.startAt),
    endAt: moment(DEFAULT_DATES.endAt),
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isMobile = useIsSize(Sizes.SM);
  const dispatch = useAppDispatch();

  const response = useQuery(AUDIT_VIEW_QUERY, {
    variables: {
      types: action ? [action] : undefined,
      user: entity?.id,
      startDate: dates.startAt?.format('YYYY-MM-DD'),
      endDate: dates.endAt?.format('YYYY-MM-DD'),
    },
  });

  const { data, loading } = response;

  const fetchMore = ConnectionHelper.generateFetchMore(
    AUDIT_VIEW_QUERY,
    response,
    'currentSession.organization.auditLogs',
  );

  const auditLogs = _.compact(
    data?.currentSession?.organization.auditLogs?.edges?.map(
      (datum) => datum?.node,
    ),
  );

  const provisions = data?.currentSession.organization.provisions ?? [];

  const filterProps = useMemo(
    () => ({
      action,
      setAction,
      entity,
      setEntity,
      onDatesChange: setDates,
    }),
    [action, entity, setAction, setEntity, setDates],
  );

  useEffect(() => {
    if (isMobile && isFilterOpen) {
      dispatch(
        UIActions.openModal(AuditFilter, {
          ...filterProps,
          asModal: true,
          close: () => {
            setIsFilterOpen(false);
            dispatch(UIActions.closeModal());
          },
        }),
      );
    }
  }, [dispatch, isFilterOpen, filterProps, isMobile]);

  return (
    <SettingsContainerView
      title="Audit Logs"
      titleRight={
        isMobile && (
          <Button
            variant="secondary"
            onClick={() => setIsFilterOpen(true)}
            icon={filterIcon}
          >
            Filter
          </Button>
        )
      }
    >
      {!isMobile && <AuditFilter {...filterProps} />}

      {loading && (
        <div className={styles.loadingContainer}>
          <LoadingIndicator />
        </div>
      )}
      {!loading && auditLogs.length > 0 && (
        <>
          <Cards>
            {auditLogs.map((auditLog) => (
              <Card key={auditLog.id} noPadding>
                <AuditLogRow auditLog={auditLog} provisions={provisions} />
              </Card>
            ))}
          </Cards>
          <InfiniteScrollWaypoint fetchMore={fetchMore} />
        </>
      )}
      {!loading && auditLogs.length === 0 && (
        <div className={styles.emptyStateContainer}>
          <EmptyState
            imageName="notFound"
            title="No results found"
            message="Try applying different filters to see more results."
          />
        </div>
      )}
    </SettingsContainerView>
  );
};

export default AuditLogContentView;
